import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface SemesterDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  showOnlyForCurrent?: boolean; // Show only when isCurrent is true
  isCurrent?: boolean; // Whether the education is current
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

// Global semester mapping that gets populated from API
let globalSemesterMapping: { [key: string]: string } = {};

// Utility function to get semester label from code
export const getSemesterLabel = (semesterCode: string): string => {
  if (!semesterCode) return '';
  
  // First try to get from global mapping (populated by API)
  if (globalSemesterMapping[semesterCode]) {
    return globalSemesterMapping[semesterCode];
  }
  
  // Fallback to the code itself if no mapping found
  return semesterCode;
};

// Function to update global semester mapping
export const updateSemesterMapping = (semesters: any[]) => {
  globalSemesterMapping = {};
  semesters.forEach(item => {
    if (item.value && item.label) {
      globalSemesterMapping[item.value] = item.label;
    }
  });
};

const SemesterDropdown: React.FC<SemesterDropdownProps> = ({
  value,
  onChange,
  placeholder = "Select Semester",
  disabled = false,
  showOnlyForCurrent = false,
  isCurrent = false,
  onDropdownLoad
}) => {
  const [semesterOptions, setSemesterOptions] = useState<{key: string, label: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const loadSemesterOptions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const semesters = await FiltersService.getDropdownItems(DROPDOWN_CODES.Semester);
      
      if (!semesters || semesters.length === 0) {
        setError('No semester options available');
        message.warning('No semester options available from the server');
      } else {
        const allOption = { key: '', label: 'All' };
        const mappedOptions = [allOption, ...semesters.map(item => ({ 
          key: item.value || '', 
          label: item.label || item.value || ''
        }))];
        setSemesterOptions(mappedOptions);
        
        // Update global mapping for use in getSemesterLabel function
        updateSemesterMapping(semesters);
      }
      
      if (onDropdownLoad) onDropdownLoad();
    } catch (error) {
      console.error('Failed to load semester options:', error);
      setError('Failed to load semester options');
      message.error('Failed to load semester options. Please try again.');
      if (onDropdownLoad) onDropdownLoad();
    } finally {
      setLoading(false);
    }
  }, [onDropdownLoad]);

  useEffect(() => {
    loadSemesterOptions();
  }, [loadSemesterOptions]);

  const handleSemesterChange = (semesterCode: string) => {
    onChange(semesterCode);
  };

  // Don't render if showOnlyForCurrent is true and isCurrent is false
  if (showOnlyForCurrent && !isCurrent) {
    return null;
  }

  // DEBUG LOG for dropdown value debugging
  console.log('SemesterDropdown value:', value, 'options:', semesterOptions, 'loading:', loading, 'error:', error);

  return (
    <div>
      <SimpleDropdown
        options={semesterOptions}
        value={value}
        onChange={handleSemesterChange}
        placeholder={loading ? "Loading semesters..." : error ? "No options available" : placeholder}
        disabled={disabled || loading || !!error}
      />
      {error && (
        <div className="text-danger small mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default SemesterDropdown; 